/**
 * Typed REST API surface. Each exported function delegates to a shared
 * `RestClient` instance. The client is lazily created on first use so the
 * device id is generated exactly once per browser session.
 *
 * POL-581: replaces the POL-580 stubs. `mount()` (in `./mount`) stays a
 * stub until POL-582 lands the Shadow DOM renderer.
 */
import { RestClient, type RestClientOptions } from "./rest-client";

export interface ClientOptions {
  baseUrl?: string;
  signal?: AbortSignal;
}

/**
 * POL-788: every public REST v1 response is wrapped in `{ data: T }`
 * (see `apps/backend/src/rest/README.md:50` and the per-route 200
 * schemas in `apps/backend/openapi/rest-v1.json`). The widget's REST
 * client returns the parsed body verbatim, so each public getter below
 * unwraps `data` and returns the inner DTO. The unwrap is per-method
 * rather than generic in `rest-client.ts` so non-enveloped routes
 * (e.g. `/health`) can remain unwrapped without a special case.
 */
type Envelope<T> = { data: T };

function unwrap<T>(body: Envelope<T> | T): T {
  if (body && typeof body === "object" && "data" in (body as Envelope<T>)) {
    return (body as Envelope<T>).data;
  }
  // Defensive: surface the malformed-shape rather than silently returning
  // an envelope-typed object as the inner DTO. Any 2xx that reaches here
  // without `data` is a contract drift the consumer needs to know about.
  throw new TypeError(
    "REST envelope missing 'data' field — check the OpenAPI spec and backend response shape",
  );
}

export type VoteOption = "A" | "B";

/**
 * Shallow response shapes. The REST surface itself is owned by POL-556;
 * these are intentionally loose so the widget can layer stricter types in
 * later tickets without a surface break.
 */
export interface PolstSummary {
  id: string;
  title?: string;
  [key: string]: unknown;
}

export interface PolstResults {
  polstId: string;
  votesA: number;
  votesB: number;
  [key: string]: unknown;
}

export interface VoteResult {
  ok: true;
  recorded: boolean;
  [key: string]: unknown;
}

export interface CampaignSummary {
  id: string;
  [key: string]: unknown;
}

export interface CampaignProgress {
  campaignId: string;
  completedPolstIds: string[];
  [key: string]: unknown;
}

/**
 * List-endpoint response shape. Mirrors the REST wire shape exactly:
 * `{ data: T[], nextCursor: string | null }`. We expose what the wire
 * delivers — no field renames, no remapping. (Pre-0.7.3 this type
 * declared `.items`; 0.7.3 renames to `.data` to match the wire and
 * remove the silent type/runtime gap that hit the polst-embedding
 * /sdk/ demo via `feed.items.length` → undefined.)
 */
export interface BrandListResponse<TItem> {
  data: TItem[];
  nextCursor: string | null;
}

export interface BrandFeedItem {
  id: string;
  kind: "polst" | "campaign";
  [key: string]: unknown;
}

let defaultClient: RestClient | null = null;
let defaultClientOptions: RestClientOptions = {};

function getDefaultClient(): RestClient {
  if (!defaultClient) {
    defaultClient = new RestClient(defaultClientOptions);
  }
  return defaultClient;
}

/**
 * Configure the module-scope REST client. Useful for tests or for apps
 * that want to set a one-time `baseUrl`. Resets any previously cached
 * default client.
 */
export function configureRestClient(options: RestClientOptions): void {
  defaultClientOptions = options;
  defaultClient = null;
}

/**
 * Test-only reset. Intentionally prefixed with `__` to keep it out of
 * the public surface the script/SDK consumers rely on.
 */
export function __resetDefaultClient(): void {
  defaultClient = null;
  defaultClientOptions = {};
}

function clientFor(options: ClientOptions | undefined): {
  client: RestClient;
  request: { baseUrl?: string; signal?: AbortSignal };
} {
  return {
    client: getDefaultClient(),
    request: { baseUrl: options?.baseUrl, signal: options?.signal },
  };
}

export function getPolst(id: string, options?: ClientOptions): Promise<PolstSummary> {
  const { client, request } = clientFor(options);
  return client
    .get<Envelope<PolstSummary>>(`/api/rest/v1/polsts/${encodeURIComponent(id)}`, request)
    .then(unwrap);
}

export function getPolstResults(
  id: string,
  options?: ClientOptions,
): Promise<PolstResults> {
  const { client, request } = clientFor(options);
  return client
    .get<Envelope<PolstResults>>(
      `/api/rest/v1/polsts/${encodeURIComponent(id)}/results`,
      request,
    )
    .then(unwrap);
}

export function vote(
  id: string,
  option: VoteOption,
  options?: ClientOptions,
): Promise<VoteResult> {
  const { client, request } = clientFor(options);
  return client
    .vote<Envelope<VoteResult>>(
      `/api/rest/v1/polsts/${encodeURIComponent(id)}/votes`,
      { option },
      request,
    )
    .then(unwrap);
}

/** Alias kept for symmetry with `voteCampaign` and the documented surface. */
export const votePolst = vote;

export function getCampaign(
  id: string,
  options?: ClientOptions,
): Promise<CampaignSummary> {
  const { client, request } = clientFor(options);
  return client
    .get<Envelope<CampaignSummary>>(
      `/api/rest/v1/campaigns/${encodeURIComponent(id)}`,
      request,
    )
    .then(unwrap);
}

export function getCampaignProgress(
  id: string,
  options?: ClientOptions,
): Promise<CampaignProgress> {
  const { client, request } = clientFor(options);
  return client
    .get<Envelope<CampaignProgress>>(
      `/api/rest/v1/campaigns/${encodeURIComponent(id)}/progress`,
      request,
    )
    .then(unwrap);
}

export function voteCampaign(
  campaignId: string,
  polstId: string,
  option: VoteOption,
  options?: ClientOptions,
): Promise<VoteResult> {
  const { client, request } = clientFor(options);
  return client
    .vote<Envelope<VoteResult>>(
      `/api/rest/v1/campaigns/${encodeURIComponent(campaignId)}/votes`,
      { polstId, option },
      request,
    )
    .then(unwrap);
}

// List endpoints (`/brands/{slug}/{polsts,campaigns,feed}`) return the
// envelope shape directly: `{ data: T[], nextCursor }`. We don't unwrap
// — `BrandListResponse<T>` mirrors the wire 1:1, so callers get
// `{ data, nextCursor }` back and can iterate `feed.data` / paginate
// via `feed.nextCursor`. Single-resource endpoints (`getPolst`, etc.)
// still go through `unwrap` because their wire shape really is a pure
// `{ data: T }` envelope worth stripping.
export function getBrandPolsts(
  brandId: string,
  options?: ClientOptions,
): Promise<BrandListResponse<PolstSummary>> {
  const { client, request } = clientFor(options);
  return client.get<BrandListResponse<PolstSummary>>(
    `/api/rest/v1/brands/${encodeURIComponent(brandId)}/polsts`,
    request,
  );
}

export function getBrandCampaigns(
  brandId: string,
  options?: ClientOptions,
): Promise<BrandListResponse<CampaignSummary>> {
  const { client, request } = clientFor(options);
  return client.get<BrandListResponse<CampaignSummary>>(
    `/api/rest/v1/brands/${encodeURIComponent(brandId)}/campaigns`,
    request,
  );
}

export function getBrandFeed(
  brandId: string,
  options?: ClientOptions,
): Promise<BrandListResponse<BrandFeedItem>> {
  const { client, request } = clientFor(options);
  return client.get<BrandListResponse<BrandFeedItem>>(
    `/api/rest/v1/brands/${encodeURIComponent(brandId)}/feed`,
    request,
  );
}
